/**
 * CountryCodePicker — Round 40o
 *
 * A compact dial-code selector designed to sit *next to* a phone number
 * input. Renders the selected country's flag emoji + ISO-style dial
 * code ("🇮🇳 +91") and opens a searchable popover with the full list.
 *
 * Curated list of ~40 high-frequency countries (covers >95% of typical
 * SaaS signups). Sorted alphabetically inside the popover for predictable
 * scanning; `popular` countries are pinned at the top for one-tap access.
 *
 * Props:
 *   value      — current ISO-2 country code, e.g. 'IN'
 *   onChange   — (iso2, dialCode) => void
 *   testId     — root data-testid (default 'country-code-picker')
 *
 * Why not a third-party lib?
 *   `react-phone-number-input` / `intl-tel-input` add ~80–120 kB to the
 *   bundle. For ~40 entries + an emoji flag, a hand-rolled picker is
 *   cheaper and visually matches the rest of the app.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';

export const COUNTRY_DIAL_CODES = [
    // Popular first — pinned to the top of the dropdown.
    { iso2: 'IN', name: 'India',           dial: '+91',  flag: '🇮🇳', popular: true },
    { iso2: 'US', name: 'United States',   dial: '+1',   flag: '🇺🇸', popular: true },
    { iso2: 'GB', name: 'United Kingdom',  dial: '+44',  flag: '🇬🇧', popular: true },
    { iso2: 'CA', name: 'Canada',          dial: '+1',   flag: '🇨🇦', popular: true },
    { iso2: 'AU', name: 'Australia',       dial: '+61',  flag: '🇦🇺', popular: true },
    { iso2: 'AE', name: 'United Arab Emirates', dial: '+971', flag: '🇦🇪', popular: true },
    { iso2: 'SG', name: 'Singapore',       dial: '+65',  flag: '🇸🇬', popular: true },

    // Rest of curated list.
    { iso2: 'BD', name: 'Bangladesh',      dial: '+880', flag: '🇧🇩' },
    { iso2: 'BR', name: 'Brazil',          dial: '+55',  flag: '🇧🇷' },
    { iso2: 'CH', name: 'Switzerland',     dial: '+41',  flag: '🇨🇭' },
    { iso2: 'CN', name: 'China',           dial: '+86',  flag: '🇨🇳' },
    { iso2: 'DE', name: 'Germany',         dial: '+49',  flag: '🇩🇪' },
    { iso2: 'DK', name: 'Denmark',         dial: '+45',  flag: '🇩🇰' },
    { iso2: 'EG', name: 'Egypt',           dial: '+20',  flag: '🇪🇬' },
    { iso2: 'ES', name: 'Spain',           dial: '+34',  flag: '🇪🇸' },
    { iso2: 'FR', name: 'France',          dial: '+33',  flag: '🇫🇷' },
    { iso2: 'HK', name: 'Hong Kong',       dial: '+852', flag: '🇭🇰' },
    { iso2: 'ID', name: 'Indonesia',       dial: '+62',  flag: '🇮🇩' },
    { iso2: 'IE', name: 'Ireland',         dial: '+353', flag: '🇮🇪' },
    { iso2: 'IL', name: 'Israel',          dial: '+972', flag: '🇮🇱' },
    { iso2: 'IT', name: 'Italy',           dial: '+39',  flag: '🇮🇹' },
    { iso2: 'JP', name: 'Japan',           dial: '+81',  flag: '🇯🇵' },
    { iso2: 'KE', name: 'Kenya',           dial: '+254', flag: '🇰🇪' },
    { iso2: 'KR', name: 'South Korea',     dial: '+82',  flag: '🇰🇷' },
    { iso2: 'LK', name: 'Sri Lanka',       dial: '+94',  flag: '🇱🇰' },
    { iso2: 'MX', name: 'Mexico',          dial: '+52',  flag: '🇲🇽' },
    { iso2: 'MY', name: 'Malaysia',        dial: '+60',  flag: '🇲🇾' },
    { iso2: 'NG', name: 'Nigeria',         dial: '+234', flag: '🇳🇬' },
    { iso2: 'NL', name: 'Netherlands',     dial: '+31',  flag: '🇳🇱' },
    { iso2: 'NP', name: 'Nepal',           dial: '+977', flag: '🇳🇵' },
    { iso2: 'NZ', name: 'New Zealand',     dial: '+64',  flag: '🇳🇿' },
    { iso2: 'OM', name: 'Oman',            dial: '+968', flag: '🇴🇲' },
    { iso2: 'PH', name: 'Philippines',     dial: '+63',  flag: '🇵🇭' },
    { iso2: 'PK', name: 'Pakistan',        dial: '+92',  flag: '🇵🇰' },
    { iso2: 'PL', name: 'Poland',          dial: '+48',  flag: '🇵🇱' },
    { iso2: 'QA', name: 'Qatar',           dial: '+974', flag: '🇶🇦' },
    { iso2: 'RU', name: 'Russia',          dial: '+7',   flag: '🇷🇺' },
    { iso2: 'SA', name: 'Saudi Arabia',    dial: '+966', flag: '🇸🇦' },
    { iso2: 'SE', name: 'Sweden',          dial: '+46',  flag: '🇸🇪' },
    { iso2: 'TH', name: 'Thailand',        dial: '+66',  flag: '🇹🇭' },
    { iso2: 'TR', name: 'Turkey',          dial: '+90',  flag: '🇹🇷' },
    { iso2: 'TW', name: 'Taiwan',          dial: '+886', flag: '🇹🇼' },
    { iso2: 'VN', name: 'Vietnam',         dial: '+84',  flag: '🇻🇳' },
    { iso2: 'ZA', name: 'South Africa',    dial: '+27',  flag: '🇿🇦' },
];

const POPULAR = COUNTRY_DIAL_CODES.filter((c) => c.popular);
const REST    = COUNTRY_DIAL_CODES
    .filter((c) => !c.popular)
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name));

export function findCountryByIso2(iso2) {
    if (!iso2) return null;
    return COUNTRY_DIAL_CODES.find((c) => c.iso2.toUpperCase() === iso2.toUpperCase()) || null;
}

export default function CountryCodePicker({
    value,
    onChange,
    testId = 'country-code-picker',
}) {
    const selected = findCountryByIso2(value) || POPULAR[0];
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const rootRef = useRef(null);

    // Click-outside to close.
    useEffect(() => {
        if (!open) return;
        const onClick = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [open]);

    const q = query.trim().toLowerCase();
    const matches = useMemo(() => {
        if (!q) return null;
        const test = (c) =>
            c.name.toLowerCase().includes(q) ||
            c.dial.replace('+', '').includes(q.replace('+', '')) ||
            c.iso2.toLowerCase().includes(q);
        return COUNTRY_DIAL_CODES.filter(test);
    }, [q]);

    const pick = (c) => {
        onChange?.(c.iso2, c.dial);
        setOpen(false);
        setQuery('');
    };

    return (
        <div className="relative" ref={rootRef} data-testid={testId}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                data-testid={`${testId}-trigger`}
                className="h-11 min-w-[100px] inline-flex items-center justify-between gap-1.5 px-2.5 border-b border-marigold/20 hover:border-marigold/40 focus:border-marigold focus:outline-none text-parchment bg-transparent transition-colors"
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span className="text-lg leading-none" aria-hidden>{selected.flag}</span>
                <span className="text-sm font-medium">{selected.dial}</span>
                <ChevronDown size={14} className="text-slate-500" />
            </button>

            {open && (
                <div
                    className="absolute z-50 mt-1 w-72 max-h-72 overflow-y-auto bg-obsidian border border-marigold/30 shadow-lg shadow-black/40 rounded-sm"
                    data-testid={`${testId}-popover`}
                    role="listbox"
                >
                    <div className="sticky top-0 bg-obsidian p-2 border-b border-marigold/15">
                        <div className="relative">
                            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
                            <input
                                autoFocus
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search country or code"
                                data-testid={`${testId}-search`}
                                className="w-full pl-7 pr-2 py-1.5 text-sm bg-surface border border-marigold/15 text-parchment placeholder:text-slate-600 focus:outline-none focus:border-marigold/50 rounded-sm"
                            />
                        </div>
                    </div>

                    {matches ? (
                        matches.length > 0 ? (
                            matches.map((c) => <Row key={c.iso2} c={c} onPick={pick} selected={selected.iso2 === c.iso2} testId={testId} />)
                        ) : (
                            <div className="px-3 py-6 text-center text-xs text-slate-500" data-testid={`${testId}-empty`}>
                                No match.
                            </div>
                        )
                    ) : (
                        <>
                            <Section label="Popular" />
                            {POPULAR.map((c) => <Row key={c.iso2} c={c} onPick={pick} selected={selected.iso2 === c.iso2} testId={testId} />)}
                            <Section label="All countries" />
                            {REST.map((c) => <Row key={c.iso2} c={c} onPick={pick} selected={selected.iso2 === c.iso2} testId={testId} />)}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

function Section({ label }) {
    return (
        <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest text-slate-500 select-none">
            {label}
        </div>
    );
}

function Row({ c, onPick, selected, testId }) {
    return (
        <button
            type="button"
            onClick={() => onPick(c)}
            data-testid={`${testId}-option-${c.iso2}`}
            role="option"
            aria-selected={selected}
            className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-marigold/10 transition-colors ${
                selected ? 'bg-marigold/10 text-marigold' : 'text-parchment'
            }`}
        >
            <span className="flex items-center gap-2 min-w-0">
                <span className="text-base leading-none" aria-hidden>{c.flag}</span>
                <span className="truncate">{c.name}</span>
            </span>
            <span className="text-slate-500 font-mono">{c.dial}</span>
        </button>
    );
}
